import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { getProjectCardImage } from '../utils/projectImages';
import './Home.css';

const Home = ({ projects = [] }) => {
  const [currentBanner, setCurrentBanner] = useState(0);
  
  // Use process.env.PUBLIC_URL for proper path resolution
  const getBannerPath = (filename) => {
    return `${process.env.PUBLIC_URL || ''}/Hero Banner/${filename}`;
  };

  // Get team member image path
  const getTeamMemberImage = (name) => {
    return `${process.env.PUBLIC_URL || ''}/Teams/${name}.jpg`;
  };

  const banners = [
    getBannerPath('Hero Banner 1.jpg'),
    getBannerPath('Hero Banner 2.png'),
    getBannerPath('Hero Banner 3.png')
  ];

  // Get first project for banner 2
  const getFirstProject = () => {
    if (!projects || projects.length === 0) return null;
    return projects[0];
  };

  const firstProject = getFirstProject();

  // Banner content configuration
  const bannerContent = [
    {
      title: "Welcome to Karmatara Nirman Sewa",
      description: "Building district highways and institutional buildings for more than two decades. Our services span across whole Nepal from Udayapur in East to Jajarkot in Far West.",
      buttonText: "VIEW MORE",
      buttonLink: "/gallery"
    },
    {
      title: "Our Signature Project",
      description: "We built Sindhuligadhi War Museum for our client Nepal Army. It carries history and culture. It serves as a tourist destination to more than 20,000 tourists each month.",
      buttonText: "Explore Other Projects",
      buttonLink: "/gallery"
    },
    {
      title: "We Excel In",
      description: "We do both government and private construction works. We also rent earth moving equipment. Feel free to reach out if you need excavators, backhoe loaders, dump trucks, tractors, etc.",
      buttonText: "LEARN MORE",
      buttonLink: "/about"
    }
  ];

  const nextBanner = () => {
    setCurrentBanner((prev) => (prev + 1) % banners.length);
  };

  const prevBanner = () => {
    setCurrentBanner((prev) => (prev - 1 + banners.length) % banners.length);
  };

  // Get 3 most recent projects by end date
  const getRecentProjects = () => {
    if (!projects || projects.length === 0) return [];
    
    const sortedProjects = [...projects]
      .filter(p => p.endDate)
      .sort((a, b) => {
        // Parse dates (assuming format MM/DD/YYYY)
        const dateA = new Date(a.endDate);
        const dateB = new Date(b.endDate);
        return dateB - dateA; // Most recent first
      })
      .slice(0, 3);
    
    return sortedProjects;
  };

  const recentProjects = getRecentProjects();
  const currentContent = bannerContent[currentBanner];

  return (
    <div className="home">
      <section className="hero-section">
        <img 
          src={banners[currentBanner]} 
          alt="Hero Banner" 
          className="hero-background-image"
          onError={(e) => {
            console.error('Failed to load image:', banners[currentBanner]);
            e.target.style.display = 'none';
          }}
        />
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <button className="hero-nav-btn hero-nav-prev" onClick={prevBanner}>
            ‹
          </button>
          <div className="hero-text">
            <h1 className="hero-title">
              {currentContent.title}
            </h1>
            <p className="hero-description hero-description-single">
              {currentContent.description}
            </p>
            <Link to={currentContent.buttonLink} className="btn btn-primary">
              {currentContent.buttonText}
            </Link>
          </div>
          <button className="hero-nav-btn hero-nav-next" onClick={nextBanner}>
            ›
          </button>
        </div>
      </section>

      <section className="values-section">
        <div className="values-container">
          <h2 className="section-title">Our Values</h2>
          <div className="values-grid">
            <div className="value-card">
              <div className="value-icon">⏱️</div>
              <h3 className="value-title">Timely Delivery</h3>
            </div>
            <div className="value-card">
              <div className="value-icon">💬</div>
              <h3 className="value-title">Effective Communication</h3>
            </div>
            <div className="value-card">
              <div className="value-icon">🏗️</div>
              <h3 className="value-title">Quality Work</h3>
            </div>
          </div>
        </div>
      </section>

      {recentProjects.length > 0 && (
        <section className="projects-preview-section">
          <div className="projects-preview-container">
            <h2 className="section-title">Our Recent Projects</h2>
            <div className="projects-preview-grid">
              {recentProjects.map(project => {
                const cardImage = getProjectCardImage(project.photoDirectory);
                return (
                  <Link
                    key={project.id}
                    to={`/project/${project.id}`}
                    className="project-preview-card"
                  >
                    <div className="project-preview-image">
                      <div className="project-type-badge">{project.type || 'Project'}</div>
                      {cardImage ? (
                        <img 
                          src={cardImage}
                          alt={project.name}
                          className="project-preview-img"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : null}
                      <div className="project-preview-overlay">
                        <span className="project-preview-title">{project.name}</span>
                        <span className="project-preview-date">Completed: {project.endDate}</span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
            <div className="view-all-projects">
              <Link to="/gallery" className="btn btn-secondary">
                View All Projects
              </Link>
            </div>
          </div>
        </section>
      )}

      <section className="equipment-lease-section">
        <div className="equipment-lease-container">
          <h2 className="section-title">Construction Equipment Available on Lease</h2>
          <div className="equipment-lease-content">
            <div className="equipment-lease-info">
              <div className="lease-info-item">
                <span className="lease-label">Service Area:</span>
                <span className="lease-value">Whole Nepal</span>
              </div>
              <div className="lease-info-item">
                <span className="lease-label">Contact:</span>
                <a href="tel:+9779869115484" className="lease-value lease-link">+977-9869115484</a>
              </div>
              <div className="lease-info-item">
                <span className="lease-label">Email:</span>
                <a href="mailto:eqp.lease@karmatara.com.np" className="lease-value lease-link">eqp.lease@karmatara.com.np</a>
              </div>
            </div>
            <div className="lease-cta">
              <p className="lease-cta-text">Feel free to contact us for equipment rental services</p>
            </div>
          </div>
        </div>
      </section>

      <section className="message-section">
        <div className="message-container">
          <h2 className="section-title">Message from Managing Director</h2>
          <div className="message-content">
            <div className="message-layout">
              <div className="message-image-container">
                <img 
                  src={`${process.env.PUBLIC_URL || ''}/images/MD photo.jpg`}
                  alt="Ram Kumar Thapa - Managing Director"
                  className="md-photo"
                  onError={(e) => {
                    console.error('Failed to load MD photo');
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <div className="message-text">
                <p>
                  Welcome to Karmatara Nirman Sewa Pvt. Ltd. As the Managing Director, 
                  I am proud to lead a team of dedicated professionals committed to 
                  excellence in construction. Our company stands on the pillars of 
                  quality, integrity, and innovation.
                </p>
                <p>
                  With years of experience in the construction industry, we have built 
                  a reputation for delivering projects on time, within budget, and to 
                  the highest standards. Our team of skilled engineers and professionals 
                  work tirelessly to ensure every project meets and exceeds expectations.
                </p>
                <p>
                  We believe in sustainable construction practices and are committed to 
                  contributing to Nepal's infrastructure development. Thank you for 
                  considering Karmatara Nirman Sewa for your construction needs.
                </p>
                <p className="signature">
                  <strong>Ram Kumar Thapa</strong>
                  <span className="signature-title">Managing Director</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="team-section">
        <div className="team-container">
          <h2 className="section-title">Our Team</h2>
          <div className="team-grid">
            <div className="team-member">
              <img 
                src={getTeamMemberImage('Ram Kumar Thapa')}
                alt="Ram Kumar Thapa"
                className="member-icon"
                onError={(e) => {
                  console.error('Failed to load team member image');
                  e.target.style.display = 'none';
                }}
              />
              <h3 className="member-name">Ram Kumar Thapa</h3>
              <p className="member-role">Managing Director / CEO</p>
            </div>
            <div className="team-member">
              <img 
                src={getTeamMemberImage('Ashish Thapa')}
                alt="Ashish Thapa"
                className="member-icon"
                onError={(e) => {
                  console.error('Failed to load team member image');
                  e.target.style.display = 'none';
                }}
              />
              <h3 className="member-name">Ashish Thapa</h3>
              <p className="member-role">Civil and Structural Engineer</p>
            </div>
            <div className="team-member">
              <img 
                src={getTeamMemberImage('Sasi Thapa')}
                alt="Sasi Thapa"
                className="member-icon"
                onError={(e) => {
                  console.error('Failed to load team member image');
                  e.target.style.display = 'none';
                }}
              />
              <h3 className="member-name">Sasi Thapa</h3>
              <p className="member-role">Civil And Highway Engineer</p>
            </div>
            <div className="team-member">
              <img 
                src={getTeamMemberImage('Laxmi Karki')}
                alt="Laxmi Karki"
                className="member-icon"
                onError={(e) => {
                  console.error('Failed to load team member image');
                  e.target.style.display = 'none';
                }}
              />
              <h3 className="member-name">Laxmi Karki</h3>
              <p className="member-role">Senior Accountant</p>
            </div>
            <div className="team-member">
              <img 
                src={getTeamMemberImage('Sujan Thapa')}
                alt="Sujan Thapa"
                className="member-icon"
                onError={(e) => {
                  console.error('Failed to load team member image');
                  e.target.style.display = 'none';
                }}
              />
              <h3 className="member-name">Sujan Thapa</h3>
              <p className="member-role">Junior Accountant</p>
            </div>
            <div className="team-member">
              <img 
                src={getTeamMemberImage('Kishan Thapa')}
                alt="Kishan Thapa"
                className="member-icon"
                onError={(e) => {
                  console.error('Failed to load team member image');
                  e.target.style.display = 'none';
                }}
              />
              <h3 className="member-name">Kishan Thapa</h3>
              <p className="member-role">Civil and Structural Engineer</p>
            </div>
            <div className="team-member">
              <img 
                src={getTeamMemberImage('Sushil Raut')}
                alt="Sushil Raut"
                className="member-icon"
                onError={(e) => {
                  console.error('Failed to load team member image');
                  e.target.style.display = 'none';
                }}
              />
              <h3 className="member-name">Sushil Raut</h3>
              <p className="member-role">IT Engineer</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
